<script setup>
/**
 * The clasp, offered once as a paper slip clipped to the first written page
 * (journal redesign 2026-10-27), and the PIN chosen on it, twice. After the
 * offer is answered the clasp lives in the page's ⋯ menu.
 */
import { computed } from 'vue'
import JournalGlyph from './JournalGlyph.vue'

const props = defineProps({
  step: { type: String, default: 'offer' },   // 'offer' | 'pin1' | 'pin2' | 'done'
  pin: { type: String, default: '' },
  err: { type: String, default: '' },
  bio: { type: String, default: '' },          // 'Face ID' … or '' when no passkey here
  animate: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
})
const emit = defineEmits(['add', 'not-now', 'digit', 'lock-now'])

const opens = computed(() => (props.bio ? `it opens with ${props.bio}, or with a PIN when ${props.bio} can’t` : 'it opens with a PIN only you know'))
</script>

<template>
  <div class="slip" :class="{ slipin: animate }" data-testid="journal-clasp-slip">
    <template v-if="step === 'offer'">
      <h4>A clasp for your journal</h4>
      <p>Only you read this journal. Add a clasp and {{ opens }}.</p>
      <div class="row">
        <button type="button" class="btn-ink" :disabled="busy" data-testid="journal-clasp-yes" @click="emit('add')"><JournalGlyph name="lock" />Add a clasp</button>
        <button type="button" class="btn-ghost" data-testid="journal-clasp-not-now" @click="emit('not-now')">Not now</button>
      </div>
      <p class="fine">You can add one later from the ⋯ menu on any page.</p>
    </template>
    <template v-else-if="step === 'pin1' || step === 'pin2'">
      <h4>{{ step === 'pin1' ? 'Choose a 4-digit PIN' : 'Once more' }}</h4>
      <p>{{ step === 'pin1' ? (bio ? `For the times ${bio} can’t open it.` : 'It opens your journal.') : 'Enter the same PIN again.' }}</p>
      <div class="dots" :aria-label="`${pin.length} of 4 digits`"><i v-for="i in 4" :key="i" :class="{ f: i <= pin.length }"></i></div>
      <p class="err" role="alert">{{ err }}</p>
      <div class="pad">
        <button v-for="d in [1, 2, 3, 4, 5, 6, 7, 8, 9]" :key="d" type="button" :disabled="busy" :data-testid="`journal-setpin-${d}`" @click="emit('digit', String(d))">{{ d }}</button>
        <span></span>
        <button type="button" :disabled="busy" data-testid="journal-setpin-0" @click="emit('digit', '0')">0</button>
        <button type="button" class="sm" :disabled="busy" aria-label="Delete a digit" @click="emit('digit', 'del')">⌫</button>
      </div>
    </template>
    <template v-else-if="step === 'done'">
      <h4>Your journal has a clasp</h4>
      <p>It closes when you leave the Journal, or after 5 minutes away. Forgot the PIN? A sign-in code by email opens it.</p>
      <div class="row"><button type="button" class="btn-ghost" data-testid="journal-clasp-close-now" @click="emit('lock-now')"><JournalGlyph name="lock" />Close it now</button></div>
    </template>
  </div>
</template>

<style scoped>
.slip { position: relative; margin: 16px 4px 10px 6px; padding: 16px 16px 14px; background: #fbf7ee; border-radius: 3px; transform: rotate(-.6deg); box-shadow: 0 6px 16px rgba(42, 24, 69, .18); }
.slip::before { content: ''; position: absolute; left: 22px; top: -12px; width: 14px; height: 34px; border: 2.5px solid #9aa0a8; border-radius: 8px; border-bottom-color: transparent; transform: rotate(8deg); box-sizing: border-box; }
h4 { margin: 0 0 4px; font: 600 21px var(--j-serif); color: var(--j-ink); }
p { margin: 0; font: 500 16px/1.4 var(--j-serif); color: var(--j-ink2); }
.row { display: flex; gap: 12px; align-items: center; margin-top: 12px; flex-wrap: wrap; }
.fine { font: 500 12.5px/1.4 var(--j-ui); color: var(--j-ink3); margin-top: 10px; }
.btn-ink { white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; min-height: 42px; border-radius: 999px; background: linear-gradient(180deg, #6d3fa3, #4f2a80); color: #ffe7b5; font: 700 15px var(--j-serif); box-shadow: 0 2px 6px rgba(42, 24, 69, .3); border: 0; cursor: pointer; }
.btn-ink :deep(svg), .btn-ghost :deep(svg) { width: 16px; height: 16px; }
.btn-ghost { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; min-height: 42px; border-radius: 999px; border: 1.3px solid rgba(42, 24, 69, .45); color: var(--j-ink); font: 600 14.5px var(--j-serif); background: none; cursor: pointer; }
.dots { display: flex; gap: 12px; justify-content: center; margin: 12px 0 8px; color: var(--j-ink); }
.dots i { width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid currentColor; display: block; box-sizing: border-box; }
.dots i.f { background: currentColor; }
.err { color: #8a2340; text-align: center; font: 500 13px var(--j-ui); min-height: 18px; }
.pad { display: grid; grid-template-columns: repeat(3, 58px); gap: 6px 18px; justify-content: center; }
.pad button { height: 44px; border-radius: 50%; font: 300 22px var(--j-ui); color: var(--j-ink); background: none; border: 0; cursor: pointer; }
.pad button:hover { background: rgba(42, 24, 69, .06); }
.pad button.sm { font: 600 13px var(--j-ui); }
.slipin { animation: slipin .7s 1.5s cubic-bezier(.2, .8, .25, 1) both; }
@keyframes slipin { from { opacity: 0; transform: translateY(18px) rotate(-3deg); } }
</style>
